import Loader from 'Components/Common/Loader';
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchConnectedIGAccounts, initIGConnection, linkIGAccount, submitAccessToken } from 'services/api/social-account';
import { fetchSocialAccountsThunk } from 'slices/auth/thunk';
import { AppDispatch, RootState } from 'store';
import { initFacebookSDK } from 'utils/facebook';

const ConnectIG = () => {
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const [connecting, setConnecting] = useState(false);
    const [accountsFetched, setAccountsFetched] = useState(false);
    const [connectedAccounts, setConnectedAccounts] = useState<any[]>([]);
    const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
    const [connectedAccount, setConnectedAccount] = useState<string | null>(null);
    const [scope, setScope] = useState<string | null>(null);
    const [isSDKReady, setSDKReady] = useState(false);
    const [connectButtonLabel, setConnectButtonLabel] = useState("Connect Instagram");
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [searchParams] = useSearchParams();
    const socialAccountId = searchParams.get('social_account_id');

    const intendedRedirect =
        (location.state as any)?.from || "/social-account";

    const feature = (location.state as any)?.feature;

    const navigate = useNavigate();
    const dispatch:AppDispatch = useDispatch();

    const fetchRequiredIGPermissions =  async () => {
        try {
            setLoading(true);
            if (!selectedAgencyId) throw new Error("No agency selected");
            const { permissions } = await initIGConnection(selectedAgencyId, socialAccountId, feature);
            const scope_ = Array.isArray(permissions) ? permissions.join(',') : '';
            setScope(scope_.trim());
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
           
    }

    useEffect(() => {
        if(scope === ""){
            // IG connection not required
            navigate(intendedRedirect)
        }
    }, [scope])

    useEffect(() => {
        if(!selectedAgencyId) return;
        initFacebookSDK().then((ready) => {
            setSDKReady(ready);
        })
        fetchRequiredIGPermissions();
    }, [selectedAgencyId])

    const handleInstagramLogin = async () => {
        try {
            setConnecting(true);
            setConnectButtonLabel("Connecting...");

            if (!isSDKReady) {
                throw new Error("Facebook SDK failed to initialize");
            }

            if (!selectedAgencyId) throw new Error("No agency selected");

            if (!scope) {
                throw new Error("No permissions returned from server");
            }

            (window as any).FB.login(
                (response: any) => {
                    if (response.authResponse) {
                        const { accessToken } = response.authResponse ?? {};
                        if (accessToken) {
                            try {
                                setConnectButtonLabel("Fetching...");
                                submitAccessToken(selectedAgencyId, accessToken).then(() => {
                                    fetchConnectedIGAccounts(selectedAgencyId).then(({ result }) => {
                                        if (result && Array.isArray(result.accounts)) {
                                            const accounts = result.accounts;
                                            setConnectedAccounts(accounts);
                                            setAccountsFetched(true);
                                            setConnecting(false)
                                        }
                                    });
                                });
                            } catch (error) {
                                console.log(error);
                            }

                        }
                    }
                },
                {
                    scope
                }
            );
        } catch (error) {
            console.log(error);
            setConnecting(false);
        }

    };

    const linkInstagramAccount = async () => {
        if(!selectedAgencyId || !selectedAccountId) return;

        try {
            setConnecting(true);
            setConnectButtonLabel("Connecting...");
            const response = await linkIGAccount(selectedAgencyId, selectedAccountId);
            if(response.status){
                setConnectedAccount(selectedAccountId);
                dispatch(fetchSocialAccountsThunk({force: true}));
                navigate(intendedRedirect);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setConnecting(false);
            setConnectButtonLabel("Connected");
        }
    }

    useEffect(() => {
        if(connectedAccounts.length === 1) {
            setSelectedAccountId(connectedAccounts[0].id);
        }
    }, [connectedAccounts])

    if(loading) return <Loader />

    return (
        <div className="page">
            <div className="page-heading">
                <div className="content pt-0 d-flex w-100 justify-content-between align-items-center">
                    <h4 className="page-title">Connect Instagram</h4>
                </div>
            </div>
            <div className="content">
                <div className="row g-4" >
                    <div className="col-md-6 d-flex flex-column gap-4">
                        <div className="wallet-card h-100 m-0">
                            <div className="wallet-card-body">
                                <h5 className="card-title">How to Connect Instagram?</h5>
                                <div className="card-text">
                                    <ol className="ps-3">
                                        <li>Click on the "Connect Instagram" button.</li>
                                        <li>A Facebook login popup will appear. Log in with your Facebook account that is linked to your Instagram.</li>
                                        <li>Grant the requested permissions to allow access to your Instagram data.</li>
                                        <li>Once connected, you can manage and track your Instagram campaigns directly from the platform.</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 d-flex flex-column gap-4">
                        <div className="wallet-card h-100 m-0">
                            {
                                accountsFetched ? (
                                    <>
                                        {
                                            connectedAccounts.length > 0 ? (
                                                <div className="wallet-card-body h-100">
                                                    <h5 className="card-title">Select Instagram Account</h5>
                                                    <div className="card-text wh-100 d-flex flex-column gap-3 justify-content-between">
                                                        <div>
                                                        <ul className="list-group mt-3">
                                                            {connectedAccounts.map((account) => (
                                                                <div onClick={() => setSelectedAccountId(account.id)} key={account.id} className={`d-flex align-items-center gap-3 connect-ig-account-item justify-content-between ${selectedAccountId === account.id ? 'active' : ''}`}>
                                                                    <div className='d-flex align-items-center gap-3'>
                                                                    <div className="position-relative">
                                                                        <img src={account.profile_picture_url} className="inf-profile-pic" style={{border: '1px solid red', height: '50px', width: '50px'}} />
                                                                        <div className="ig-logo-space">
                                                                            <div className="ig-logo"></div>
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <h6 className='m-0'>{account.name}</h6>
                                                                        <span className="text-muted">@{account.username}</span>
                                                                    </div>
                                                                    </div>
                                                                    {
                                                                        selectedAccountId === account.id && <span className='connected-badge'>Selected</span>
                                                                    }
                                                                </div>

                                                            ))}
                                                        </ul>
                                                        <div className="d-flex align-items-center justify-content-end mt-2">
                                                            <span onClick={() => setAccountsFetched(false)} className="cursor-pointer text-primary">Can't find your account? Reconnect.</span>
                                                        </div>
                                                        </div>


                                                        <button disabled={!selectedAccountId || connecting || !!connectedAccount} onClick={linkInstagramAccount} className='btn btn-gradient'>{connecting ? connectButtonLabel : "Connect"}</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="wallet-card-body">
                                                    <h5 className="card-title">No Instagram Accounts Connected</h5>
                                                    <p className="card-text">You have not connected any Instagram accounts yet. Please connect an account to manage your campaigns.</p>
                                                </div>
                                            )
                                        }
                                    </>) : (
                                    <div className="wallet-card-body">
                                        <h5 className="card-title">Connect Instagram Account</h5>
                                        <p className="card-text">Connect your Instagram account to manage and track your campaigns seamlessly.</p>
                                        <button disabled={connecting} className="btn btn-gradient d-flex gap-2 align-items-center" onClick={handleInstagramLogin}> <i className='mdi mdi-instagram fs-5'></i> {connecting ? connectButtonLabel : "Connect Instagram"}</button>
                                    </div>
                                )
                            }

                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ConnectIG
